import { create } from 'zustand';
import type { TwoFactorSetupDto, TwoFactorStatusDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * The signed-in person's own two-step sign-in (ADR-116): its state, a setup in
 * progress, and recovery codes shown once.
 */
interface SecurityState {
  status: TwoFactorStatusDto | null;
  setup: TwoFactorSetupDto | null;
  /** Shown once, until dismissed. */
  codes: string[] | null;
  loading: boolean;
  submitting: boolean;
  error: string | null;
  load: () => Promise<void>;
  startSetup: () => Promise<void>;
  cancelSetup: () => void;
  enable: (code: string) => Promise<void>;
  renewCodes: (code: string) => Promise<void>;
  disable: (code: string) => Promise<void>;
  dismissCodes: () => void;
}

export const useSecurityStore = create<SecurityState>((set, get) => {
  /** One call, its error kept for the page. */
  async function run(call: () => Promise<Partial<SecurityState>>): Promise<void> {
    set({ submitting: true, error: null });
    try {
      set({ ...(await call()), submitting: false });
    } catch (error) {
      set({ submitting: false, error: errorMessage(error, 'Something went wrong. Please try again.') });
    }
  }

  return {
    status: null,
    setup: null,
    codes: null,
    loading: false,
    submitting: false,
    error: null,

    async load() {
      set({ loading: true, error: null });
      try {
        set({ status: await withSession(() => adminApi.me.twoFactor.status()), loading: false });
      } catch (error) {
        set({ loading: false, error: errorMessage(error, 'Could not load.') });
      }
    },

    startSetup: () => run(async () => ({ setup: await withSession(() => adminApi.me.twoFactor.setup()) })),

    cancelSetup() {
      set({ setup: null, error: null });
    },

    enable: (code) =>
      run(async () => {
        const { recoveryCodes } = await withSession(() => adminApi.me.twoFactor.enable(code));
        const status = await withSession(() => adminApi.me.twoFactor.status());
        return { codes: recoveryCodes, setup: null, status };
      }),

    renewCodes: (code) =>
      run(async () => {
        const { recoveryCodes } = await withSession(() => adminApi.me.twoFactor.renewRecoveryCodes(code));
        const status = get().status;
        return { codes: recoveryCodes, status: status ? { ...status, recoveryCodesLeft: recoveryCodes.length } : status };
      }),

    disable: (code) => run(async () => ({ status: await withSession(() => adminApi.me.twoFactor.disable(code)), codes: null })),

    dismissCodes() {
      set({ codes: null });
    },
  };
});
